import React from 'react';
import { ActionIcon, Tooltip, Avatar, type ActionIconProps } from '@mantine/core';
import { FontAwesomeIcon, type FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router-dom';
import { AppTheme, noProfilePicture } from '../../../../appTheme';
import styles from './StyledActionButton.module.scss';

export type StyledActionButtonProps = (ActionIconProps & Partial<FontAwesomeIconProps>) & {
  tooltip?: string;
  // Optional avatar source (renders avatar instead of icon when provided)
  avatarSrc?: string | null | undefined;
  to?: any;
};

const StyledActionButton: React.FC<StyledActionButtonProps> = ({
  tooltip,
  avatarSrc,
  icon,
  children,
  style,
  ...rest
}) => {
  // default foreground color and size if not provided
  const size = (rest as any).size ?? 'lg';
  const fgColor = (rest as any).color ?? '#ffffff';

  const content = children
    ? children
    : avatarSrc !== undefined
      ? (
          <Avatar
            src={avatarSrc || noProfilePicture()}
            alt="Profile picture"
            radius="xl"
            size={typeof size === 'number' ? 'sm' : 'sm'}
            color={avatarSrc ? undefined : "initials"}
            // Ensure large images shrink to fit within the avatar circle without cropping
            styles={{ image: { objectFit: 'contain' } }}
          />
        )
      : icon
        ? <FontAwesomeIcon icon={icon as any} />
        : null;

  const componentProp = (rest as any).component ?? ((rest as any).to ? (Link as any) : undefined);

  const button = (
    <ActionIcon
      className={styles['action-button']}
      variant="subtle"
      style={{ color: fgColor, ...(style)}}
      styles={{ root: { '&:hover': { backgroundColor: AppTheme.colors.themePurple } } }}
      component={componentProp as any}
      {...rest}
    >
      {content}
    </ActionIcon>
  );

  return tooltip ? (
    <Tooltip label={tooltip} position="right" color="dark">
      {button}
    </Tooltip>
  ) : (
    <>{button}</>
  );
};

export default StyledActionButton;
